import { makeAutoObservable, runInAction } from "mobx";
import { rain, RadarImageInfo } from "@/endpoints/rain/rain";
import { AsyncStateStore } from "@/utils/AsyncStateStore";

export class RadarTimelineStore {
	timestamps: string[] = [];
	images = new Map<string, RadarImageInfo>();
	selectedIndex = -1;
	loading = false;
	playing = false;
	urlCache = new Map<string, string>();

	private loadId = 0;
	private playTimer: ReturnType<typeof setInterval> | null = null;
	private timestepsApi = new AsyncStateStore(rain.getTimesteps);
	private imagesApi = new AsyncStateStore(rain.getImages);
	private imageUrlApi = new AsyncStateStore(rain.getImageUrl);

	constructor() {
		makeAutoObservable(this);
	}

	get selectedTimestamp(): string | null {
		if (this.selectedIndex < 0 || this.selectedIndex >= this.timestamps.length) return null;
		return this.timestamps[this.selectedIndex];
	}

	get selectedImage(): RadarImageInfo | null {
		const ts = this.selectedTimestamp;
		if (!ts) return null;
		// Try exact match first
		const exact = this.images.get(ts);
		if (exact) return exact;
		// Fallback: find closest image by time
		const target = new Date(ts).getTime();
		let best: RadarImageInfo | null = null;
		let bestDiff = Infinity;
		for (const img of this.images.values()) {
			const diff = Math.abs(new Date(img.timestamp).getTime() - target);
			if (diff < bestDiff) {
				bestDiff = diff;
				best = img;
			}
		}
		return best;
	}

	async loadTimeline() {
		const id = ++this.loadId;
		this.loading = true;

		try {
			const tsResp = await this.timestepsApi.fetch({ productType: "RATE", limit: 96 });
			if (id !== this.loadId) return;

			const timesteps = [...tsResp.timesteps].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
			if (timesteps.length === 0) {
				runInAction(() => {
					this.loading = false;
				});
				return;
			}

			const imagesResp = await this.imagesApi.fetch({
				productType: "RATE",
				start: timesteps[0],
				end: timesteps[timesteps.length - 1],
				limit: 1000,
			});
			if (id !== this.loadId) return;

			const imageList = Array.isArray(imagesResp) ? imagesResp : (imagesResp.data ?? []);

			runInAction(() => {
				this.timestamps = timesteps;
				this.images.clear();
				for (const img of imageList) {
					this.images.set(img.timestamp, img);
				}
				this.selectedIndex = timesteps.length - 1;
				this.loading = false;
			});
		} catch {
			if (id === this.loadId) {
				runInAction(() => {
					this.loading = false;
				});
			}
		}
	}

	selectIndex(i: number) {
		this.selectedIndex = Math.max(0, Math.min(i, this.timestamps.length - 1));
	}

	togglePlay() {
		if (this.playing) {
			this.pause();
		} else {
			this.play();
		}
	}

	private play() {
		if (this.timestamps.length === 0) return;
		this.playing = true;
		// If at the end, restart from beginning
		if (this.selectedIndex >= this.timestamps.length - 1) {
			this.selectedIndex = 0;
		}
		this.playTimer = setInterval(() => {
			runInAction(() => {
				if (this.selectedIndex >= this.timestamps.length - 1) {
					this.pause();
				} else {
					this.selectedIndex++;
				}
			});
		}, 500);
	}

	pause() {
		this.playing = false;
		if (this.playTimer) {
			clearInterval(this.playTimer);
			this.playTimer = null;
		}
	}

	async getImageUrl(imageId: string): Promise<string | null> {
		const cached = this.urlCache.get(imageId);
		if (cached) return cached;

		try {
			const resp = await this.imageUrlApi.fetch(imageId);
			runInAction(() => {
				this.urlCache.set(imageId, resp.url);
			});
			return resp.url;
		} catch {
			return null;
		}
	}

	clear() {
		this.pause();
		this.loadId++;
		this.timestamps = [];
		this.images.clear();
		this.selectedIndex = -1;
		this.loading = false;
		this.urlCache.clear();
	}
}
